import type { ReactNode } from "react"

import { Button, cn, Divider } from "@coval/ui"

import { DateTimeRangePicker } from "@/components/date-time-range-picker/DateTimeRangePicker"
import type { DatePreset } from "@/types/DatePreset"

export interface DateRangeBounds {
  start: Date | null
  end: Date | null
}

interface DateRangePanelProps<TPreset extends DatePreset> {
  /** Applied range: fills the calendar and the time fields. */
  value: DateRangeBounds
  /** Receives every edit to the custom range, including partial ones. */
  onRangeChange: (range: DateRangeBounds) => void
  /** Quick Select windows, offered above the custom range. */
  presets: TPreset[]
  /**
   * Applies a picked preset. Callers that persist the choice take this to keep
   * the preset's identity; the default applies the range the preset computes.
   */
  onPresetSelect?: (preset: TPreset) => void
  /** Omit to hide the clear action, on surfaces that always carry a range. */
  onClear?: () => void
  /** Names the clear action, for surfaces that filter on a named time field. */
  clearLabel?: string
  /** Freezes the presets and the range inputs. */
  dateOnly?: boolean
  disabled?: boolean
  /** Shown under the custom range, e.g. why an edit was not applied. */
  customRangeError?: string | null
  /** Hide the "Times are local" hint, where the surface says so already. */
  hideTimezoneHint?: boolean
  /** Extra sections below the custom range, e.g. surface-level defaults. */
  renderExtraSections?: () => ReactNode
  /**
   * `popover` is the standalone control behind a trigger: padded sections with
   * headings. `inline` drops that chrome for surfaces that nest the range among
   * other filters and label it themselves.
   */
  variant?: "popover" | "inline"
  /** Presets sit two across by default; three suits short labels. */
  presetColumns?: 2 | 3
  /** Labels each preset for assistive tech, e.g. "Set label date range to 7 days". */
  presetAriaLabel?: (preset: TPreset) => string
}

/**
 * The date range control itself: Quick Select presets over a custom range.
 *
 * Split out of `DateRangeFilterButton` so surfaces that cannot open a popover
 * of their own — a filter list is already inside one — offer the same presets
 * and the same range editor instead of rebuilding them.
 */
export function DateRangePanel<TPreset extends DatePreset>({
  value,
  onRangeChange,
  presets,
  onPresetSelect,
  onClear,
  clearLabel = "Clear Date Range",
  disabled = false,
  dateOnly = false,
  customRangeError,
  hideTimezoneHint = false,
  renderExtraSections,
  variant = "popover",
  presetColumns = 2,
  presetAriaLabel,
}: DateRangePanelProps<TPreset>) {
  const isPopover = variant === "popover"
  const hasAppliedRange = Boolean(value.start || value.end)

  const handlePresetClick = (preset: TPreset) => {
    if (onPresetSelect) {
      onPresetSelect(preset)
      return
    }
    onRangeChange(preset.getValue())
  }

  return (
    <>
      <div className={isPopover ? "p-4" : undefined}>
        {isPopover && (
          <div className="text-sm font-semibold mb-3">Quick Select</div>
        )}
        <div
          className={cn(
            "grid gap-2",
            presetColumns === 3 ? "grid-cols-3" : "grid-cols-2",
          )}
        >
          {presets.map((preset) => (
            <Button
              key={preset.label}
              variant="secondary"
              onClick={() => handlePresetClick(preset)}
              disabled={disabled}
              className={isPopover ? "justify-start" : "!px-2"}
              aria-label={presetAriaLabel?.(preset)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {isPopover && <Divider />}

      <div className={isPopover ? "p-4" : "mt-2"}>
        {isPopover && (
          <div className="text-sm font-semibold mb-3">Custom Range</div>
        )}
        <DateTimeRangePicker
          startDate={value.start}
          endDate={value.end}
          onChange={(start, end) => onRangeChange({ start, end })}
          isLoading={disabled}
          dateOnly={dateOnly}
          hideTimezoneHint={hideTimezoneHint}
          className="flex-col items-stretch gap-4"
        />
        {customRangeError && (
          <p className="text-xs text-coval-danger-text mt-2">
            {customRangeError}
          </p>
        )}
      </div>

      {renderExtraSections?.()}

      {onClear && hasAppliedRange && (
        <>
          {isPopover && <Divider />}
          <div className={isPopover ? "p-4" : undefined}>
            <Button
              variant="secondary"
              onClick={onClear}
              className="w-full"
              aria-label={clearLabel}
            >
              {clearLabel}
            </Button>
          </div>
        </>
      )}
    </>
  )
}
